/**
 * Network Status indicator (Tradesea top bar; SPEC §17.1 principle 1 "connection health visible
 * on every screen"). Derived from the trading-socket connection store; pure so it is testable.
 */

export type NetworkKind = "connected" | "connecting" | "unstable" | "disconnected";

export type NetworkStatus = {
  kind: NetworkKind;
  /** Tradesea label text. */
  label: string;
  /** Longer explanation for the tooltip / screen readers. */
  detail: string;
};

/** Browser↔gateway RTT above this reads as an unstable connection. */
export const UNSTABLE_RTT_MS = 400;

/** The subset of `ConnectionState` (packages/core stream/store.ts) this needs. */
export type ConnectionLike = {
  status: "disconnected" | "connecting" | "connected";
  gwRttMs: number | null;
  reconnectAttempt: number;
  lastError: string | null;
};

export function networkStatus(c: ConnectionLike): NetworkStatus {
  if (c.status === "connected") {
    if (c.gwRttMs !== null && c.gwRttMs > UNSTABLE_RTT_MS) {
      return {
        kind: "unstable",
        label: "Connection unstable",
        detail: `Gateway round trip ${String(c.gwRttMs)} ms (above ${String(UNSTABLE_RTT_MS)} ms)`,
      };
    }
    return {
      kind: "connected",
      label: "Connected",
      detail: c.gwRttMs === null ? "Gateway connected" : `Gateway round trip ${String(c.gwRttMs)} ms`,
    };
  }
  if (c.status === "connecting") {
    // A reconnect after a drop is a degraded state, not a first connect.
    if (c.reconnectAttempt > 0) {
      return {
        kind: "unstable",
        label: "Connection unstable",
        detail: `Reconnecting (attempt ${String(c.reconnectAttempt + 1)})${c.lastError ? ` — ${c.lastError}` : ""}`,
      };
    }
    return { kind: "connecting", label: "Connecting…", detail: "Connecting to the gateway" };
  }
  return {
    kind: "disconnected",
    label: "Disconnected",
    detail: c.lastError ? `Disconnected — ${c.lastError}` : "Disconnected from the gateway",
  };
}
